import { Button, TextField, Typography, withStyles } from '@material-ui/core';
import { useEffect, useRef, useState } from 'react';
import { axiosInstance, cancelToken } from '../../utils/axiosInstance';
import { endpoint, fwSettingsFields, snackbarDefaultValue, snackbarVariant } from '../../utils/constants';
import { formatFwDate } from '../../utils/fwHelpers';
import {
    fieldErrors,
    isDisabledRefusal,
    isValidLimit,
    operationKey,
    probeWorkflow,
    refusalOf,
    refusalText,
    workflowModes,
} from '../../utils/fwWorkflow';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Snackbar from '../Snackbar/Snackbar';
import TitleHeader from '../TitleHeader/TitleHeader';

const styles = {
    root: {
        margin: '1rem 2rem',
    },
    fields: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(20rem, 1fr))',
        gap: '1rem',
        marginBottom: '1rem',
    },
    meta: {
        marginBottom: '0.5rem',
    },
    actions: {
        display: 'flex',
        gap: '1rem',
        alignItems: 'center',
    },
    banner: {
        margin: '1rem 0',
    },
};

const UNPROVISIONED = 'unprovisioned';

const limitValues = settings =>
    Object.fromEntries(
        fwSettingsFields.map(field => {
            const value = settings?.[field.name];

            return [field.name, value === undefined || value === null ? '' : String(value)];
        })
    );

const FwConfig = ({ classes }) => {
    const [mode, setMode] = useState(workflowModes.LOADING);
    const isFirstPublication = mode === UNPROVISIONED;
    const [settings, setSettings] = useState(null);
    const [values, setValues] = useState({});
    const [errors, setErrors] = useState({});
    const [banner, setBanner] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [snackbarData, setSnackbarData] = useState(snackbarDefaultValue);
    const requestKey = useRef(operationKey());
    const probeSource = useRef(null);

    const showMessage = (message, variant) => {
        setSnackbarData({ isSnackbarOpen: true, message, variant, errorInfo: '' });
    };

    const handleIsSnackbarOpen = () => setSnackbarData(snackbarDefaultValue);

    const load = () => {
        setMode(workflowModes.LOADING);
        probeSource.current?.cancel();
        probeSource.current = cancelToken.source();
        probeWorkflow({ cancelToken: probeSource.current.token }).then(result => {
            if (result.mode === workflowModes.LOADING) return;
            if (result.mode === workflowModes.CONFIGURED) {
                setSettings(result.settings);
                setValues(limitValues(result.settings));
                setErrors({});
                setBanner(null);
                setMode(workflowModes.CONFIGURED);
                return;
            }
            if (
                result.mode === workflowModes.ERROR &&
                result.refusal?.status === 503 &&
                (result.refusal.code || result.refusal.message) === 'FW_SETTINGS_NOT_PROVISIONED'
            ) {
                setSettings(null);
                setValues(limitValues({ pickup_connect_seconds: 420 }));
                setErrors({});
                setBanner(null);
                setMode(UNPROVISIONED);
                return;
            }
            setMode(result.mode);
            setBanner(
                result.mode === workflowModes.ERROR
                    ? {
                          kind: 'error',
                          text: refusalText(result.refusal, 'Nie udało się połączyć z backendem'),
                      }
                    : null
            );
        });
    };

    useEffect(() => {
        load();

        return () => probeSource.current?.cancel();
    }, []);

    const handleValueChange = (name, value) => {
        requestKey.current = operationKey();
        setValues(current => ({ ...current, [name]: value }));
        setErrors(current => ({ ...current, [name]: undefined }));
    };

    const save = () => {
        const nextErrors = {};
        const limits = {};

        fwSettingsFields.forEach(field => {
            const raw = String(values[field.name] ?? '').trim();
            const number = Number(raw);
            if (raw === '' || !isValidLimit(number, field)) {
                nextErrors[field.name] = `Wartość od ${field.min} do ${field.max}`;
                return;
            }
            limits[field.name] = number;
        });
        setErrors(nextErrors);
        if (Object.keys(nextErrors).some(name => nextErrors[name])) return;

        const body = { ...limits, expected_version: isFirstPublication ? 0 : settings?.version };

        setIsSaving(true);
        axiosInstance
            .put(endpoint.FW_SETTINGS, body, {
                headers: { 'Idempotency-Key': requestKey.current },
            })
            .then(() => {
                setBanner(null);
                requestKey.current = operationKey();
                showMessage('Konfiguracja opublikowana', snackbarVariant.SUCCESS);
                load();
            })
            .catch(error => {
                const refusal = refusalOf(error);
                if (isDisabledRefusal(refusal)) {
                    setMode(workflowModes.DISABLED);
                    return;
                }
                if (refusal.status === 409) {
                    requestKey.current = operationKey();
                    if (refusal.code === 'FW_IDEMPOTENCY_CONFLICT') {
                        setBanner({
                            kind: 'error',
                            text: refusalText(
                                refusal,
                                'Operacja została już wykonana z innym zestawem danych'
                            ),
                        });
                        return;
                    }
                    setBanner({
                        kind: 'conflict',
                        text: 'Konfiguracja zmieniła się w innym miejscu. Odśwież, aby zobaczyć aktualną wersję.',
                    });
                    return;
                }
                setErrors(current => ({ ...current, ...fieldErrors(refusal) }));
                setBanner({
                    kind: 'error',
                    text: refusalText(refusal, 'Nie udało się zapisać konfiguracji'),
                });
            })
            .finally(() => setIsSaving(false));
    };

    const header = <TitleHeader title="Moduł FW — Konfiguracja" />;

    if (mode === workflowModes.LOADING) {
        return (
            <div className={classes.root}>
                {header}
                <div className={classes.actions}>
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    if (mode === workflowModes.DISABLED) {
        return (
            <div className={classes.root}>
                {header}
                <Typography variant="h6">Moduł FW jest wyłączony</Typography>
                <Typography>
                    Konfiguracja i katalog artefaktów są niedostępne, dopóki moduł nie zostanie włączony.
                </Typography>
            </div>
        );
    }

    if (mode === workflowModes.ERROR) {
        return (
            <div className={classes.root}>
                {header}
                <Typography variant="h6">Nie można odczytać konfiguracji modułu FW</Typography>
                <Typography className={classes.banner}>{banner?.text}</Typography>
                <Button variant="outlined" color="primary" onClick={load}>
                    Spróbuj ponownie
                </Button>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            {header}
            {isFirstPublication ? (
                <Typography className={classes.meta}>
                    Konfiguracja nie została jeszcze opublikowana. Uzupełnij limity, aby opublikować pierwszą
                    wersję.
                </Typography>
            ) : (
                <>
                    <Typography
                        className={classes.meta}>{`Wersja konfiguracji: ${settings?.version}`}</Typography>
                    <Typography className={classes.meta}>
                        {`Obowiązuje od: ${formatFwDate(settings?.effective_at)}`}
                    </Typography>
                    <Typography className={classes.meta}>
                        {`Autor: ${settings?.actor_snapshot?.display_identity || '—'}`}
                    </Typography>
                </>
            )}
            <div className={classes.fields}>
                {fwSettingsFields.map(field => (
                    <TextField
                        key={field.name}
                        id={`fw-setting-${field.name}`}
                        label={field.label}
                        value={values[field.name] ?? ''}
                        onChange={event => handleValueChange(field.name, event.target.value)}
                        error={Boolean(errors[field.name])}
                        helperText={errors[field.name] || `Zakres ${field.min} do ${field.max}`}
                        variant="outlined"
                    />
                ))}
            </div>
            {banner?.kind === 'conflict' && (
                <div className={classes.banner}>
                    <Typography color="error">{banner.text}</Typography>
                    <Button variant="outlined" color="primary" onClick={load}>
                        Odśwież
                    </Button>
                </div>
            )}
            {banner?.kind === 'error' && (
                <Typography className={classes.banner} color="error">
                    {banner.text}
                </Typography>
            )}
            <div className={classes.actions}>
                <Button variant="contained" color="primary" onClick={save} disabled={isSaving}>
                    Opublikuj konfigurację
                </Button>
            </div>
            <Snackbar snackbarData={snackbarData} handleIsSnackbarOpen={handleIsSnackbarOpen} />
        </div>
    );
};

export default withStyles(styles)(FwConfig);
